import React, { ReactElement, useEffect, useState } from "react";
import "./style.css";
import { HStack, VStack } from "components/Stack";
import Card from "components/Card";
import Button from "../formElements/Button/Button";
import Dialog from "../AppDialog/components/Dialog/Dialog";
import { ReactComponent as CloseIcon } from "images/close.svg";
import { ReactComponent as CheckIcon } from "images/check_icon.svg";
import {
  isSelfStudyItem,
  PricingModel,
  PricingModelType,
} from "../../model/pricingmodel";
import { getSelectedPricingModelAsync } from "../../services/pricingmodels";
import { TalaeraError } from "../../model/general";
import { getErrorComponents } from "../../services/util";
import TalaeraSpinner from "../TalaeraSpinner/TalaeraSpinner";
import { Trans, useTranslation } from "react-i18next";

export type Props = {
  children?: React.ReactNode;
  enable?: boolean;
  triggerContainerClassName?: string;
  title?: string;
  triggerWidth?: string;
  defaultOpen?: boolean;
  onUpgrade?: () => void;
  onClose?: () => void;
  dialogPortal?: (children: ReactElement) => ReactElement;
};

function ComparePlansModal(props: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<TalaeraError>();
  const [pricingModel, setPricingModel] = useState<PricingModel>();
  const { t } = useTranslation();

  const planLengthToString = (length: number) => {
    if (length === 30 || length === 31) {
      return t("Common.month");
    }
    if (length === 365) {
      return t("Common.annually");
    }
    return (
      <Trans i18nKey={"Common.everyDay"} values={{ length }}>
        every ${length} days
      </Trans>
    );
  };

  const hdUpgrade = (evt: Event) => {
    evt.preventDefault();
    if (props.onUpgrade) {
      props.onUpgrade();
      setOpen(false);
    }
  };

  const hdKeepBasic = (evt: Event) => {
    evt.preventDefault();
    handleClose();
  };

  const handleOpen = () => {
    if (!props.enable) {
      return;
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    props.onClose && props.onClose();
  };

  useEffect(() => {
    if (!open || loading || !!pricingModel) {
      return;
    }

    setLoading(true);
    getSelectedPricingModelAsync({ type: PricingModelType.SELF_STUDY })
      .then(setPricingModel)
      .catch(setErrors)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (props.defaultOpen) {
      setOpen(true);
    }
  }, [props.defaultOpen]);

  if (errors && open) {
    return getErrorComponents(errors);
  }

  if (!open) {
    return (
      <div
        className={"trigger-modal-container " + props.triggerContainerClassName || ""}
        onClick={handleOpen}
      >
        {props.children}
      </div>
    );
  }

  if (loading) {
    const dialog = (
      <Dialog
        showDialog={open}
        onClose={handleClose}
        className={"app-dialog plan-upgrade-modal"}
      >
        <div className={"close-btn"} onClick={handleClose}>
          <CloseIcon />
        </div>

        <h1 className={"upgrade-title"}>{props.title || "Upgrade to Pro"}</h1>
        <HStack justify={"center"}>
          <TalaeraSpinner />
        </HStack>
      </Dialog>
    );
    return props.dialogPortal ? props.dialogPortal(dialog) : dialog;
  }
  const plans = pricingModel?.items?.filter(isSelfStudyItem);
  if (!plans) {
    const dialog = (
      <Dialog
        showDialog={open}
        onClose={handleClose}
        className={"app-dialog plan-upgrade-modal"}
      >
        <div className={"close-btn"} onClick={handleClose}>
          <CloseIcon />
        </div>

        <h1 className={"upgrade-title"}>{props.title || "Upgrade to Pro"}</h1>
        <HStack justify={"center"}>
          There's no available subscription plans for you.
        </HStack>
      </Dialog>
    );
    return props.dialogPortal ? props.dialogPortal(dialog) : dialog;
  }

  const mainPlan = plans[0];
  const supplementalPlans = plans.slice(1);

  let dialog = (
    <Dialog
      showDialog={open}
      onClose={handleClose}
      className={"app-dialog plan-upgrade-modal"}
    >
      <div className={"close-btn"} onClick={handleClose}>
        <CloseIcon />
      </div>

      <h1 className={"upgrade-title"}>{props.title || t("Common.upgradeToPro")}</h1>

      <HStack className={"plan-row"} align={"stretch"}>
        <Card className="plan free">
          <VStack style={{ height: "100%" }} justify={"space-between"}>
            <div className={"plan-info"}>
              <p className={"name"}>{t("Common.basicPlan")}</p>
              <h3 className={"price"}>$0</h3>
              <p className={"subscription"}>{t("Common.free")}</p>

              <div className={"benefits"}>
                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.courseAccess")}</div>
                </HStack>

                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.translateTool")}</div>
                </HStack>
              </div>
            </div>

            <Button onClick={hdKeepBasic} className={"button free-btn"}>
              {t("Common.keepBasic")}
            </Button>
          </VStack>
        </Card>

        <Card className="plan pro">
          <VStack justify={"space-between"}>
            <div className={"plan-info"}>
              <p className="name">{t("Common.proPlan")}</p>
              <h3 className={"price"}>
                ${mainPlan.price}/{planLengthToString(mainPlan.length!)}
              </h3>
              {supplementalPlans.map(plan => (
                <p className={"subscription"}>
                  or ${plan.price}/{planLengthToString(plan.length!)}
                </p>
              ))}

              <div className={"benefits"}>
                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.everythingBasic")}</div>
                </HStack>

                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.allEngCourses")}</div>
                </HStack>

                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.weeklyEngGroup")}</div>
                </HStack>

                <HStack className={"benefit-item"} align={"center"}>
                  <CheckIcon />
                  <div>{t("Common.personalDict")}</div>
                </HStack>
              </div>
            </div>

            <Button className={"button pro-btn"} onClick={hdUpgrade}>
              {t("Common.upgradeToPro")}
            </Button>
          </VStack>
        </Card>
      </HStack>
    </Dialog>
  );
  dialog = props.dialogPortal ? props.dialogPortal(dialog) : dialog;

  return (
    <>
      <div
        className={"trigger-modal-container " + (props.triggerContainerClassName || "")}
        onClick={handleOpen}
      >
        {props.children}
      </div>
      {dialog}
    </>
  );
}

export default ComparePlansModal;
